import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  retry,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { EMPTY, Observable, combineLatest, forkJoin, merge, of, throwError, zip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';

import {
  ButtonFilterData,
  ButtonFilterDataName,
  DashboardAddColumnPayload,
  DashboardApiPicker,
  DashboardColumn,
  DashboardEditColumnPayload,
  DashboardFilterName,
  DashboardFilterPayload,
  DashboardFilterType,
  DashboardInstallationData,
  ERROR_SNAPSHOT_NOT_FOUND,
  EquipmentApiPicker,
  ServiceDocumentsApi,
  SortOrder,
  SubscriptionPlan,
  ViError,
  errorButtonFilterDataPayload,
  maintenanceButtonFilterDataPayload,
  offlineButtonFilterDataPayload,
  worksProperlyButtonFilterDataPayload,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { Action } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';

import { UIService } from '@services';
import { KanbanQuickViewWrapperComponent } from '../../+kanban-board/kanban-installation-list/kanban-quick-view-wrapper/kanban-quick-view-wrapper.component';
import { ActiveErrorsService } from '../../+installations/services/active-errors.service';

import { KanbanBoardActions } from './kanban-board.action';
import { KanbanBoardStore } from './kanban-board.store';
import {
  DashboardFilter,
  DashboardListFilterButtonData,
  initialNumberOfInstallationsInColumn,
  initialNumberOfInstallationsInListView,
} from './kanban-board.model';
import {
  DashboardInstallationColumn,
  SingleDashboardColumnInstallationsUpdate,
} from './kanban-board.state';
import { RefreshBoardModalComponent } from '../../+kanban-board/kanban-installation-list/kanban-installation-list-columns/refresh-board-modal/refresh-board-modal.component';
import { NavigationStart, Router } from '@angular/router';
import { KanbanBoardHelperService } from './kanban-board.helper.service';
import { KanbanBoardMappers } from './kanban-board.mappers';
import { BookingSubscriptionPackageOptions, RemoteConfigStore } from '../remote-config';
import { BOOKING_TABS, BookingActions, BookingQueryParams, BookingStore } from '../booking';
import { HighestSubscriptionPlanPipe } from '../../+kanban-board/kanban-installation-list/pipes/highest-subscription-plan/highest-subscription-plan.pipe';
import { AuthStore } from '../auth';
import { DashboardInstallationUrls } from '../../+installations/installations-routing.helper';
import { DashboardActions } from '../dashboard';
import { GatewayCommissioningActions } from '../gateway-commissioning';

@Injectable()
export class KanbanBoardEffects {
  private readonly INVALID_VERSION_ERROR = 'MODIFIED_CONCURRENTLY';
  private navigationStart$ = this.router.events.pipe(
    filter((event) => event instanceof NavigationStart)
  );

  constructor(
    private actions$: Actions,
    private dashboardApiPicker: DashboardApiPicker,
    private kanbanBoardStore: KanbanBoardStore,
    private uiService: UIService,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private equipmentApiPicker: EquipmentApiPicker,
    private overlay: Overlay,
    private serviceDocumentsApi: ServiceDocumentsApi,
    private activeErrorsService: ActiveErrorsService,
    private router: Router,
    private kanbanBoardHelperService: KanbanBoardHelperService,
    private remoteConfigStore: RemoteConfigStore,
    private bookingStore: BookingStore,
    private authStore: AuthStore
  ) {}

  getDashboard$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getDashboard.attempt),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            retry(2),
            map((response) => KanbanBoardActions.getDashboard.success({ columns: response.data })),
            catchError((error) => of(KanbanBoardActions.getDashboard.error({ error })))
          )
      )
    );
  });

  updateColumnVersions$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.addColumn.success,
        KanbanBoardActions.editColumnName.success,
        KanbanBoardActions.deleteColumn.success,
        KanbanBoardActions.setColumnSortOrder.success,
        KanbanBoardActions.moveKanbanColumn.success
      ),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            retry(2),
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map((response) =>
              KanbanBoardActions.updateColumnVersions.success({ columns: response.data })
            ),
            catchError((error) => {
              this.uiService.unblockUI();

              return of(KanbanBoardActions.updateColumnVersions.error({ error }));
            })
          )
      )
    );
  });

  closeModalColumn$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.setColumnSortOrder.success),
      map(() => KanbanBoardActions.columnDialogActions.close())
    );
  });

  openModalColumn$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(KanbanBoardActions.columnDialogActions.open),
        tap(({ modal, column }) => {
          this.dialog.open(modal, {
            width: '496px',
            disableClose: true,
            autoFocus: false,
            data: column ?? null,
          });
        })
      );
    },
    { dispatch: false }
  );

  addColumn$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.addColumn.attempt),
      concatLatestFrom(() => this.kanbanBoardStore.lastColumnId$),
      tap(() => {
        this.uiService.blockUI();
      }),
      switchMap(([action, lastColumnId]) => {
        const body: DashboardAddColumnPayload = {
          id: lastColumnId + 1,
          name: action.name,
          sortOrder: action.sortOrder,
          installations: [],
          leftNeighbor: lastColumnId,
        };

        return this.dashboardApiPicker
          .instance()
          .addColumn(body)
          .pipe(
            map((response) => {
              this.uiService.unblockUI();

              return KanbanBoardActions.addColumn.success({ column: response.data });
            }),
            catchError((error) => {
              this.uiService.unblockUI();

              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.ADD_COLUMN_MODAL.ERROR_NOTIFICATION'
                ),
              });

              return of(KanbanBoardActions.addColumn.error({ error }));
            })
          );
      })
    );
  });

  deleteColumn$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.deleteColumn.attempt, KanbanBoardActions.deleteColumn.retry),
      tap(() => {
        this.uiService.blockUI();
      }),
      concatLatestFrom((action) => this.kanbanBoardStore.getColumnVersion(action.id)),
      switchMap(([action, version]) =>
        this.dashboardApiPicker
          .instance()
          .deleteColumn(action.id, version)
          .pipe(
            retry(2),
            map(() => {
              this.uiService.unblockUI();

              return KanbanBoardActions.deleteColumn.success({ id: action.id });
            }),
            catchError((error: ViError) => {
              const errorLabel = this.translateService.instant(
                '+INSTALLATIONS.KANBAN_DASHBOARD.DELETE_COLUMN.ERROR_NOTIFICATION'
              );
              if (
                action.type === '[KANBAN BOARD Delete column] attempt' &&
                error.errorType === this.INVALID_VERSION_ERROR
              ) {
                return of(
                  KanbanBoardActions.deleteColumn.errorWithRetryAttemptAndDataFetching({
                    ...action,
                    error,
                    errorLabel,
                  })
                );
              } else {
                this.uiService.unblockUI();
                this.toastService.error({
                  message: errorLabel,
                });

                return of(KanbanBoardActions.deleteColumn.error({ error }));
              }
            })
          )
      )
    );
  });

  editColumnName$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.editColumnName.attempt, KanbanBoardActions.editColumnName.retry),
      tap(() => {
        this.uiService.blockUI();
      }),
      concatLatestFrom((action) => this.kanbanBoardStore.getColumnVersion(action.columnId)),
      switchMap(([action, version]) =>
        this.dashboardApiPicker
          .instance()
          .editColumn(action.columnId, {
            installations: action.data.installations,
            leftNeighbor: action.data.leftNeighbor,
            version,
            name: action.data.name,
            sortOrder: action.data.sortOrder,
          })
          .pipe(
            retry(2),
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map(({ data }) => {
              this.uiService.unblockUI();

              return KanbanBoardActions.editColumnName.success({
                id: action.columnId,
                editColumnPayload: {
                  installations: data.installations,
                  leftNeighbor: data.leftNeighbor,
                  name: data.name,
                  sortOrder: data.sortOrder,
                  version: data.version,
                },
              });
            }),
            catchError((error: ViError) => {
              const errorLabel = this.translateService.instant(
                '+INSTALLATIONS.KANBAN_DASHBOARD.EDIT_COLUMN.ERROR_NOTIFICATION'
              );
              if (
                action.type === '[KANBAN BOARD Edit column name] attempt' &&
                error.errorType === this.INVALID_VERSION_ERROR
              ) {
                return of(
                  KanbanBoardActions.editColumnName.errorWithRetryAttemptAndDataFetching({
                    ...action,
                    error,
                    errorLabel,
                  })
                );
              } else {
                this.uiService.unblockUI();
                this.toastService.error({
                  message: errorLabel,
                });

                return of(KanbanBoardActions.editColumnName.error({ id: action.columnId, error }));
              }
            })
          )
      )
    );
  });

  setColumnSorting$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.setColumnSortOrder.attempt,
        KanbanBoardActions.setColumnSortOrder.retry
      ),
      tap(() => {
        this.uiService.blockUI();
      }),
      concatLatestFrom(({ column }) => [
        this.kanbanBoardStore.getColumnVersion(column.id),
        this.kanbanBoardStore.selectedAndMappedFilters$,
      ]),
      switchMap(([action, version, filters]) => {
        const body: DashboardEditColumnPayload = {
          name: action.column.name,
          version,
          sortOrder: action.sortOrder,
          leftNeighbor: action.column.leftNeighbor,
          ...(action.column.selfDefined && { installations: action.column.installationIds }),
        };

        return this.dashboardApiPicker
          .instance()
          .editColumn(action.column.id, body)
          .pipe(
            mergeMap(() =>
              this.dashboardApiPicker.instance().fetchInstallationForColumn(
                {
                  columnId: action.column.id,
                  offset: 0,
                  limit: initialNumberOfInstallationsInColumn,
                  withQuickView: true,
                },
                filters,
                action.sortOrder
              )
            ),
            map(({ data }) => {
              this.uiService.unblockUI();

              return KanbanBoardActions.setColumnSortOrder.success({
                columnId: action.column.id,
                sortOrder: action.sortOrder,
                installations: data,
              });
            }),
            catchError((error: ViError) => {
              const errorLabel = this.translateService.instant(
                '+INSTALLATIONS.KANBAN_DASHBOARD.CHANGE_SORT_ORDER.ERROR_NOTIFICATION'
              );

              if (
                action.type === '[KANBAN BOARD Set column sorting] attempt' &&
                error.errorType === this.INVALID_VERSION_ERROR
              ) {
                return of(
                  KanbanBoardActions.setColumnSortOrder.errorWithRetryAttemptAndDataFetching({
                    ...action,
                    error,
                    errorLabel,
                  })
                );
              } else if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
                this.uiService.unblockUI();

                return of(KanbanBoardActions.openForcedDashboardRefreshDialog({ modalData: {} }));
              } else {
                this.uiService.unblockUI();
                this.toastService.error({
                  message: errorLabel,
                });

                return of(KanbanBoardActions.setColumnSortOrder.error({ error }));
              }
            }),
            takeUntil(this.navigationStart$)
          );
      })
    );
  });

  getFilters$: Observable<Action> = createEffect(() => {
    const kanbanAbortedActionError = 'GET /dashboard failed, do not fetch filters';
    const listAbortedActionError = 'POST /installations failed, do not fetch filters';

    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.getFilters.attempt,
        KanbanBoardActions.refreshSnapshotInInstallationList.success
      ),
      concatLatestFrom(() => this.kanbanBoardStore.filters$),
      filter(([_action, filters]) => filters.length === 0),
      switchMap(([action]) => {
        let loadedDashboard$: Observable<{}>;

        if (this.isInBoardView()) {
          // Kanban board
          loadedDashboard$ = this.kanbanBoardStore.fetchingDashboardStatus$.pipe(
            filter((status) => !status.loading && (status.loaded || status.error)),
            switchMap((status) => (status.loaded ? of({}) : throwError(kanbanAbortedActionError)))
          );
        } else {
          // List view
          loadedDashboard$ = this.kanbanBoardStore.installationListState$.pipe(
            filter((status) => !status.loading || !!status.error),
            switchMap((status) => (status.error ? throwError(listAbortedActionError) : of({})))
          );
        }

        return zip(of(action), loadedDashboard$);
      }),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getFilters()
          .pipe(
            retry(2),
            map((response) => {
              const filters: DashboardFilter[] = [];

              const ignoredFilters: Pick<DashboardFilter, 'filterName' | 'value'>[] = [
                { filterName: DashboardFilterName.ActiveAnomalies, value: 'root_cause' },
                { filterName: DashboardFilterName.ActiveAnomalies, value: 'critical' },
                { filterName: DashboardFilterName.ActiveAnomalies, value: 'information' },
              ];

              response.data.forEach((filterElement) => {
                // Value property sometimes is array of string, sometimes null which needs to be changed into true
                // flat each value and pass it in array with select and value properties
                const mappedFilter: DashboardFilter = {
                  filterName: filterElement.filterName,
                  label: filterElement.filterName,
                  type: filterElement.type,
                  value: null,
                };

                const matchingIgnoredFilters = ignoredFilters.filter(
                  (ignoredFilter) => ignoredFilter.filterName === mappedFilter.filterName
                );

                switch (filterElement.type) {
                  case DashboardFilterType.Complex:
                    filterElement.values.forEach((value) => {
                      // Ignore filters
                      if (
                        matchingIgnoredFilters.length &&
                        matchingIgnoredFilters.some(
                          (ignoredFilter) => ignoredFilter.value === value
                        )
                      ) {
                        return;
                      }

                      // Add filter
                      filters.push({
                        ...mappedFilter,
                        label: value,
                        value,
                      });
                    });
                    break;

                  case DashboardFilterType.Toggle:
                    // Ignore filters
                    if (matchingIgnoredFilters.length) {
                      return;
                    }

                    // Add filter
                    mappedFilter.value = true;
                    filters.push(mappedFilter);
                    break;

                  case DashboardFilterType.TextSearch:
                    mappedFilter.value = '';
                    filters.push(mappedFilter);
                    break;
                }
              });

              return KanbanBoardActions.getFilters.success({ filters });
            }),
            catchError((error) => of(KanbanBoardActions.getFilters.error({ error })))
          )
      ),
      catchError((error: string) => {
        return of(KanbanBoardActions.getFilters.aborted({ error }));
      })
    );
  });

  // Successful change of filter causes re-fetch whole list thus no api calls are done here
  setDashboardFilterAttempt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.setDashboardFilter.attempt),
      concatLatestFrom(() => [
        this.kanbanBoardHelperService.filtersSelectedInDropdown$,
        this.kanbanBoardHelperService.filterInputSearchValue$,
        this.kanbanBoardStore.selectedFilters$,
      ]),
      map(([_, filtersToSend, filterInputSearchValue, currentlySelectedFilters]) => {
        // All filters are merged in one object, remove text filter because it's returned in another stream
        const filterSelectedInDropdown: DashboardFilter[] = filtersToSend.filter(
          (filter) => filter.type !== DashboardFilterType.TextSearch
        );

        if (filterInputSearchValue.length > 1) {
          const textFilter: DashboardFilter = {
            filterName: DashboardFilterName.Fulltext,
            label: DashboardFilterName.Fulltext,
            type: DashboardFilterType.TextSearch,
            value: filterInputSearchValue,
          };
          filterSelectedInDropdown.push(textFilter);
        }

        if (isEqual(filterSelectedInDropdown, currentlySelectedFilters)) {
          return KanbanBoardActions.setDashboardFilter.aborted();
        }

        return KanbanBoardActions.setDashboardFilter.success({ filters: filterSelectedInDropdown });
      })
    );
  });

  selectProperEffectForFiltering$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.setDashboardFilter.success),
      map(() => {
        if (this.isInBoardView()) {
          return KanbanBoardActions.fetchKanbanDashboardWithFilters.attempt();
        }

        return KanbanBoardActions.initialFetchInstallationList.attempt();
      })
    );
  });

  fetchKanbanDashboardWithFilters$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.fetchKanbanDashboardWithFilters.attempt),
      concatLatestFrom(() => this.kanbanBoardStore.selectedAndMappedFilters$),
      switchMap(([_action, payload]) =>
        this.dashboardApiPicker
          .instance()
          .setFilters(payload)
          .pipe(
            map((response) =>
              KanbanBoardActions.fetchKanbanDashboardWithFilters.success({ columns: response.data })
            ),
            catchError((error) =>
              of(KanbanBoardActions.fetchKanbanDashboardWithFilters.error({ error }))
            )
          )
      )
    );
  });

  openQuickViewOverlay$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.openQuickViewPanel),
      switchMap(() => {
        const overlayRef = this.overlay.create({
          width: '100vw',
          hasBackdrop: true,
          scrollStrategy: this.overlay.scrollStrategies.block(),
        });

        const userProfilePortal = new ComponentPortal(KanbanQuickViewWrapperComponent);
        overlayRef.attach(userProfilePortal);

        const closeOverlayEvent$ = merge(
          this.kanbanBoardStore.forceQuickViewClose$.pipe(),
          overlayRef.backdropClick().pipe()
          // map void into something so stream emits
        ).pipe(map(() => ({})));

        return combineLatest([closeOverlayEvent$, of(overlayRef)]).pipe(take(1));
      }),
      filter(([event]) => !!event),
      map(([_event, overlayRef]) => {
        overlayRef.detach();

        return KanbanBoardActions.closeQuickViewPanel();
      })
    );
  });

  getInstallationSmartNotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getInstallationSmartNotes.attempt),
      switchMap((action) =>
        this.equipmentApiPicker
          .instance()
          .listActiveError(action.installationId)
          .pipe(
            map((response) => {
              return KanbanBoardActions.getInstallationSmartNotes.success({
                activeErrors: response.data,
              });
            }),
            catchError((error) =>
              of(KanbanBoardActions.getInstallationSmartNotes.error({ error }))
            ),
            takeUntil(this.actions$.pipe(ofType(KanbanBoardActions.closeQuickViewPanel)))
          )
      )
    );
  });

  dispatchAdditionalDataFetchForSmartNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getInstallationSmartNotes.success),
      filter((action) => action.activeErrors.length > 0),
      concatLatestFrom(() => this.kanbanBoardStore.quickViewSelectedDevice$),
      switchMap(([action, device]) => {
        const {
          countryCode,
          langCode,
          serial,
        } = this.activeErrorsService.getSearchActiveErrorParameters(device.vin);

        const filteredActiveErrors = action.activeErrors.filter(
          (smartNote) => smartNote.content?.errorCode && smartNote.content?.gatewaySerial
        );

        return filteredActiveErrors.map((smartNote) =>
          KanbanBoardActions.getQuickViewErrorDetails.attempt({
            errorCode: smartNote.content.errorCode,
            countryCode,
            langCode,
            serial,
            smartNoteId: smartNote.id,
          })
        );
      })
    );
  });

  getQuickViewErrorDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getQuickViewErrorDetails.attempt),
      concatLatestFrom(() => this.authStore.isDemo$),
      mergeMap(([{ countryCode, errorCode, langCode, serial, smartNoteId }, isDemo]) => {
        return isDemo
          ? of(KanbanBoardActions.getQuickViewErrorDetails.error({ error: null, smartNoteId }))
          : this.serviceDocumentsApi
              .listErrorsDatabase({
                countryCode,
                languageCode: langCode,
                materialNumber: serial,
                errorCode,
              })
              .pipe(
                map((errorData) =>
                  KanbanBoardActions.getQuickViewErrorDetails.success({
                    smartNote: errorData,
                    smartNoteId,
                  })
                ),
                catchError((error) => {
                  return of(
                    KanbanBoardActions.getQuickViewErrorDetails.error({ error, smartNoteId })
                  );
                }),
                takeUntil(this.actions$.pipe(ofType(KanbanBoardActions.closeQuickViewPanel)))
              );
      })
    );
  });

  fetchInstallationsForColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.fetchInstallationsForColumn.attempt),
      concatLatestFrom(() => this.kanbanBoardStore.selectedAndMappedFilters$),
      switchMap(([action, filters]) =>
        this.dashboardApiPicker
          .instance()
          .fetchInstallationForColumn(action.payload, filters, null)
          .pipe(
            map((response) =>
              KanbanBoardActions.fetchInstallationsForColumn.success({
                columnId: action.payload.columnId,
                installations: response.data,
              })
            ),
            catchError((error: ViError) => {
              if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
                return of(KanbanBoardActions.openForcedDashboardRefreshDialog({ modalData: {} }));
              } else {
                this.toastService.error({
                  message: this.translateService.instant(
                    '+INSTALLATIONS.KANBAN_DASHBOARD.FETCH_INSTALLATIONS_ERROR'
                  ),
                });
              }

              return of(
                KanbanBoardActions.fetchInstallationsForColumn.error({
                  columnId: action.payload.columnId,
                  error,
                })
              );
            }),
            takeUntil(this.navigationStart$)
          )
      )
    );
  });

  copyKanbanInstallation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.copyKanbanInstallation.attempt),
      concatLatestFrom((action) => [
        this.kanbanBoardStore.getEditColumnPayloadById(action.nextColumnId),
      ]),
      switchMap(([action, body]) => {
        return this.dashboardApiPicker
          .instance()
          .editColumn(action.nextColumnId, body)
          .pipe(
            map(({ data }) => {
              this.toastService.success({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_COPY_SUCCESS'
                ),
              });

              return KanbanBoardActions.copyKanbanInstallation.success({
                columnId: data.id,
                version: data.version,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_COPY_ERROR'
                ),
              });

              return of(
                KanbanBoardActions.copyKanbanInstallation.error({
                  error,
                  installation: action.installation,
                  nextColumnId: action.nextColumnId,
                })
              );
            })
          );
      })
    );
  });

  moveKanbanColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.moveKanbanColumn.attempt),
      concatLatestFrom((action) => [
        this.kanbanBoardStore.getEditColumnPayloadById(action.movedColumnId),
      ]),
      switchMap(([action, body]) =>
        this.dashboardApiPicker
          .instance()
          .editColumn(action.movedColumnId, body)
          .pipe(
            map(({ data }) => {
              this.toastService.success({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.COLUMN_MOVE_SUCCESS'
                ),
              });

              return KanbanBoardActions.moveKanbanColumn.success({
                columnId: data.id,
                version: data.version,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.COLUMN_MOVE_ERROR'
                ),
              });

              return of(
                KanbanBoardActions.moveKanbanColumn.error({
                  error,
                  movedColumnIndex: action.movedColumnIndex,
                  targetColumnIndex: action.targetColumnIndex,
                })
              );
            })
          )
      )
    );
  });

  // Effect triggered when MODIFIED_CONCURRENTLY error is returned
  // It happens when user doesn't use app for a long time and data in column is different
  updateColumnsAndRefetchInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.updateColumnsAndRefetchInstallations.attempt),
      concatLatestFrom(() => [
        this.kanbanBoardStore.columns$,
        this.kanbanBoardStore.selectedAndMappedFilters$,
      ]),
      switchMap(([_action, storeColumns, filters]) =>
        this.dashboardApiPicker
          .instance()
          .setFilters(filters)
          .pipe(
            map((response) => ({
              dashboard: response.data,
              columns: this.getColumnsWithVersionValueChanged(storeColumns, response.data),
            })),
            switchMap(({ columns, dashboard }) => {
              const apiCalls: Observable<SingleDashboardColumnInstallationsUpdate>[] = columns.map(
                (column) =>
                  this.dashboardApiPicker
                    .instance()
                    .fetchInstallationForColumn(
                      {
                        columnId: column.id,
                        limit: column.numberOfInstallations,
                        offset: 0,
                        withQuickView: true,
                      },
                      filters,
                      null
                    )
                    .pipe(
                      retry(2),
                      map(
                        (response): SingleDashboardColumnInstallationsUpdate => ({
                          id: column.id,
                          installations: response.data,
                          version: column.version,
                        })
                      )
                    )
              );

              return zip(forkJoin(apiCalls), of(dashboard));
            }),
            map(([columnsWithNewVersion, dashboard]) =>
              KanbanBoardActions.updateColumnsAndRefetchInstallations.success({
                columnsWithNewVersion,
                dashboard,
              })
            ),
            catchError((error) =>
              of(KanbanBoardActions.updateColumnsAndRefetchInstallations.error({ error }))
            )
          )
      )
    );
  });

  retryCallForInvalidVersionValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.deleteColumn.errorWithRetryAttemptAndDataFetching,
        KanbanBoardActions.editColumnName.errorWithRetryAttemptAndDataFetching,
        KanbanBoardActions.setColumnSortOrder.errorWithRetryAttemptAndDataFetching
      ),
      concatLatestFrom(() => [this.kanbanBoardStore.forcedStoreUpdateStatus$]),
      switchMap(([action, updateStatus]) => {
        if (!updateStatus.loading) {
          this.kanbanBoardStore.updateColumnsAndRefetchInstallations();
        }

        const isUpdateSuccessful$ = this.kanbanBoardStore.forcedStoreUpdateStatus$.pipe(
          filter(
            (status) =>
              !status.loading &&
              // rxjs was passing corrupted data only with loading flag changed
              (status.loaded || status.error)
          ),
          map((status) => status.loaded)
        );

        return combineLatest([of(action), isUpdateSuccessful$]).pipe(take(1));
      }),
      switchMap(([action, isSuccessful]) => {
        // Delete column
        if (
          action.type === '[KANBAN BOARD Delete column] error with retry attempt and data fetching'
        ) {
          if (isSuccessful) {
            return of(KanbanBoardActions.deleteColumn.retry({ ...action }));
          } else {
            this.toastService.error({ message: action.errorLabel });
            this.uiService.unblockUI();

            return of(KanbanBoardActions.deleteColumn.retryAborted({ ...action }));
          }
        }
        // Change column name
        if (
          action.type ===
          '[KANBAN BOARD Edit column name] error with retry attempt and data fetching'
        ) {
          if (isSuccessful) {
            return of(KanbanBoardActions.editColumnName.retry({ ...action }));
          } else {
            this.toastService.error({ message: action.errorLabel });

            return of(
              KanbanBoardActions.editColumnName.retryAborted({
                columnId: action.columnId,
                error: action.error,
              })
            );
          }
        }
        // Change sort order
        if (
          action.type ===
          '[KANBAN BOARD Set column sorting] error with retry attempt and data fetching'
        ) {
          if (isSuccessful) {
            return of(KanbanBoardActions.setColumnSortOrder.retry({ ...action }));
          } else {
            this.toastService.error({ message: action.errorLabel });
            this.uiService.unblockUI();

            return of(
              KanbanBoardActions.setColumnSortOrder.retryAborted({
                ...action,
              })
            );
          }
        }
      })
    );
  });

  moveKanbanInstallationCopy$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.moveKanbanInstallation.attempt),
      concatLatestFrom((action) => [
        this.kanbanBoardStore.getPayloadDataBeforeMovingInstallation(
          action.previousColumnId,
          action.nextColumnId
        ),
      ]),
      switchMap(([action, body]) => {
        return this.dashboardApiPicker
          .instance()
          .moveKanbanInstallation(body)
          .pipe(
            map((response) => {
              this.toastService.success({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_MOVE_SUCCESS'
                ),
              });

              return KanbanBoardActions.moveKanbanInstallation.success({
                columnsToUpdate: response.data,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_MOVE_ERROR'
                ),
              });

              return of(
                KanbanBoardActions.moveKanbanInstallation.error({
                  installation: action.installation,
                  nextColumnId: action.nextColumnId,
                  previousColumnId: action.previousColumnId,
                  error,
                })
              );
            })
          );
      })
    );
  });

  deleteKanbanInstallationCopy$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.deleteKanbanInstallationCopy.attempt),
      concatLatestFrom((action) => [
        this.kanbanBoardStore.getEditColumnPayloadById(action.columnId),
      ]),
      switchMap(([action, body]) => {
        return this.dashboardApiPicker
          .instance()
          .editColumn(action.columnId, body)
          .pipe(
            map(({ data }) => {
              this.toastService.success({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_DELETE_SUCCESS'
                ),
              });

              return KanbanBoardActions.deleteKanbanInstallationCopy.success({
                columnId: data.id,
                version: data.version,
              });
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.DRAG_DROP.INSTALLATION_DELETE_ERROR'
                ),
              });

              return of(
                KanbanBoardActions.deleteKanbanInstallationCopy.error({
                  error,
                  columnId: action.columnId,
                })
              );
            })
          );
      })
    );
  });

  // createNewColumnAndMoveInstallationToIt
  createNewColumnBeforeMovingInstallation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationAttempt),
      tap(() => {
        this.uiService.blockUI();
      }),
      concatLatestFrom(() => [
        this.kanbanBoardStore.lastColumnId$,
        this.kanbanBoardStore.installationOfTemporaryColumn$,
      ]),
      switchMap(([action, lastColumnId, installationToMove]) => {
        const body: DashboardAddColumnPayload = {
          id: lastColumnId + 1,
          name: action.newColumnName,
          sortOrder: SortOrder.asc,
          installations: [],
          leftNeighbor: lastColumnId,
        };

        return this.dashboardApiPicker
          .instance()
          .addColumn({
            ...body,
          })
          .pipe(
            map((response) => {
              return KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationSuccess(
                {
                  column: response.data,
                  installation: installationToMove,
                }
              );
            }),
            catchError((error) => {
              this.uiService.unblockUI();
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.ADD_COLUMN_MODAL.ERROR_NOTIFICATION'
                ),
              });

              return of(
                KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationFailed({
                  error,
                })
              );
            })
          );
      })
    );
  });

  moveInstallationAfterSuccessfulColumnCreation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.createNewColumnAndMoveInstallationToIt.columnCreationSuccess),
      concatLatestFrom(() => [
        this.kanbanBoardStore.idOfOriginalColumnDuringColumnCreationAndMoveProcess$,
      ]),
      map(([action, previousColumnId]) =>
        KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationAttempt({
          installation: action.installation,
          nextColumnId: action.column.id,
          previousColumnId,
        })
      )
    );
  });

  createNewColumnAndMoveInstallationToIt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationAttempt),
      concatLatestFrom((action) => [
        this.kanbanBoardStore.getPayloadDataBeforeMovingInstallation(
          action.previousColumnId,
          action.nextColumnId
        ),
      ]),
      switchMap(([action, body]) =>
        this.dashboardApiPicker
          .instance()
          .moveKanbanInstallation(body)
          .pipe(
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map((response) => {
              this.toastService.success({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.COLUMN_ADDED_MOVE_INSTALLATION_SUCCESS'
                ),
              });

              return KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationSuccessful(
                { columnsToUpdate: response.data }
              );
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.COLUMN_ADDED_MOVE_INSTALLATION_ERROR'
                ),
              });

              return of(
                KanbanBoardActions.createNewColumnAndMoveInstallationToIt.moveInstallationFailed({
                  error,
                  installation: action.installation,
                  nextColumnId: action.nextColumnId,
                  previousColumnId: action.previousColumnId,
                })
              );
            })
          )
      )
    );
  });

  createNewColumnAndCopyInstallationToIt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationAttempt),
      tap(() => {
        this.uiService.blockUI();
      }),
      concatLatestFrom(() => [
        this.kanbanBoardStore.lastColumnId$,
        this.kanbanBoardStore.installationOfTemporaryColumn$,
      ]),
      switchMap(([action, lastColumnId, installationToCopy]) => {
        const body: DashboardAddColumnPayload = {
          id: lastColumnId + 1,
          name: action.newColumnName,
          sortOrder: SortOrder.asc,
          installations: [installationToCopy.id],
          leftNeighbor: lastColumnId,
        };

        return this.dashboardApiPicker
          .instance()
          .addColumn({
            ...body,
          })
          .pipe(
            finalize(() => {
              this.uiService.unblockUI();
            }),
            map((response) => {
              return KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationSuccess(
                {
                  column: response.data,
                  installation: installationToCopy,
                }
              );
            }),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.ADD_COLUMN_MODAL.ERROR_NOTIFICATION'
                ),
              });

              return of(
                KanbanBoardActions.createNewColumnAndCopyInstallationToIt.columnCreationFailed({
                  error,
                })
              );
            })
          );
      })
    );
  });

  initialFetchInstallationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.initialFetchInstallationList.attempt),
      tap(() => this.uiService.blockUI()),
      concatLatestFrom(() => [this.kanbanBoardStore.selectedAndMappedFilters$]),
      mergeMap(([_action, filters]) => {
        return this.getInstallationListAndItsLimit(filters).pipe(
          tap(() => this.uiService.unblockUI()),
          map(({ installations, cursor }) => {
            return KanbanBoardActions.initialFetchInstallationList.success({
              installations,
              cursor,
            });
          }),
          catchError((error: ViError) => {
            this.uiService.unblockUI();
            if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
              return of(
                KanbanBoardActions.initialFetchInstallationList.snapshotOutdatedError({
                  limit: 0,
                  offset: initialNumberOfInstallationsInListView,
                })
              );
            }

            return of(KanbanBoardActions.initialFetchInstallationList.error({ error }));
          }),
          takeUntil(this.navigationStart$)
        );
      })
    );
  });

  retryInitialFetchInstallationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.retryInitialFetchInstallationList.attempt),
      concatLatestFrom(() => [this.kanbanBoardStore.selectedAndMappedFilters$]),
      mergeMap(([_action, filters]) =>
        this.getInstallationListAndItsLimit(filters).pipe(
          map(({ installations, cursor }) => {
            return KanbanBoardActions.retryInitialFetchInstallationList.success({
              installations,
              cursor,
            });
          }),
          catchError((error: ViError) => {
            return of(KanbanBoardActions.retryInitialFetchInstallationList.error({ error }));
          })
        )
      )
    );
  });

  getButtonFilterData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.getButtonFilterData.attempt,
        KanbanBoardActions.getButtonFilterData.retry,
        KanbanBoardActions.refreshSnapshotInInstallationList.success
      ),
      mergeMap((action) =>
        this.dashboardApiPicker
          .instance()
          .getInstallationListFilterButtonData()
          .pipe(
            map(({ data }) => this.extendButtonFilterData(data)),
            map((buttonFilterData) => {
              return KanbanBoardActions.getButtonFilterData.success({
                buttonFilterData,
              });
            }),
            catchError((error: ViError) => {
              if (
                error.errorType === ERROR_SNAPSHOT_NOT_FOUND &&
                action.type === '[KANBAN BOARD Get button filter data] attempt'
              ) {
                return of(KanbanBoardActions.getButtonFilterData.snapshotOutdatedError());
              }

              return of(KanbanBoardActions.getButtonFilterData.error());
            }),
            takeUntil(this.navigationStart$)
          )
      )
    );
  });

  retryGetButtonFilterData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getButtonFilterData.snapshotOutdatedError),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            map(() => KanbanBoardActions.getButtonFilterData.retry()),
            catchError(() => of(KanbanBoardActions.getButtonFilterData.error()))
          )
      )
    );
  });

  fetchInstallationListAfterFetchingButtonData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.getButtonFilterData.success),
      map(() => KanbanBoardActions.initialFetchInstallationList.attempt())
    );
  });

  nextFetchInstallationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.nextFetchInstallationList.attempt),
      concatLatestFrom(() => [
        this.kanbanBoardStore.selectedAndMappedFilters$,
        this.kanbanBoardStore.installationListCursor$,
      ]),
      switchMap(([action, filters, cursor]) =>
        this.dashboardApiPicker
          .instance()
          .getFilteredInstallationsV2(action.limit, filters, cursor)
          .pipe(
            map(({ data, cursor }) =>
              KanbanBoardActions.nextFetchInstallationList.success({
                installations: data,
                cursor: cursor.next,
              })
            ),
            catchError((error: ViError) => {
              if (error.errorType === ERROR_SNAPSHOT_NOT_FOUND) {
                return of(
                  KanbanBoardActions.nextFetchInstallationList.snapshotOutdatedError({
                    limit: action.limit,
                    offset: action.offset,
                  })
                );
              }

              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.FETCH_INSTALLATIONS_ERROR'
                ),
              });

              return of(KanbanBoardActions.nextFetchInstallationList.error({ error }));
            }),
            takeUntil(this.navigationStart$)
          )
      )
    );
  });

  retryNextFetchInstallationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.retryNextFetchInstallationList.attempt),
      concatLatestFrom(() => [
        this.kanbanBoardStore.selectedAndMappedFilters$,
        this.kanbanBoardStore.installationListCursor$,
      ]),
      switchMap(([action, filters, cursor]) =>
        this.dashboardApiPicker
          .instance()
          .getFilteredInstallationsV2(action.limit, filters, cursor)
          .pipe(
            map(({ data, cursor }) =>
              KanbanBoardActions.retryNextFetchInstallationList.success({
                installations: data,
                cursor: cursor.next,
              })
            ),
            catchError((error) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.KANBAN_DASHBOARD.FETCH_INSTALLATIONS_ERROR'
                ),
              });

              return of(KanbanBoardActions.retryNextFetchInstallationList.error({ error }));
            })
          )
      )
    );
  });

  checkFilterButtonDataPresenceBeforeFetchingInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.checkFilterButtonDataPresenceBeforeFetchingInstallations),
      concatLatestFrom(() => this.kanbanBoardStore.buttonFilterData$),
      map(([_action, buttonData]) => {
        return !buttonData
          ? KanbanBoardActions.getButtonFilterData.attempt()
          : KanbanBoardActions.initialFetchInstallationList.attempt();
      })
    );
  });

  snapshotOutdatedError$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.nextFetchInstallationList.snapshotOutdatedError),
      map(() =>
        KanbanBoardActions.openForcedDashboardRefreshDialog({ modalData: { listView: true } })
      )
    );
  });

  refreshSnapshotInInstallationList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.refreshSnapshotInInstallationList.attempt,
        KanbanBoardActions.initialFetchInstallationList.snapshotOutdatedError
      ),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .getDashboard()
          .pipe(
            map(() => KanbanBoardActions.refreshSnapshotInInstallationList.success()),
            catchError(() => of(KanbanBoardActions.refreshSnapshotInInstallationList.error()))
          )
      )
    );
  });

  fetchInstallationListAfterSelectingFilterButton$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.applyFiltersByClickingButton),
      map(() => KanbanBoardActions.initialFetchInstallationList.attempt())
    );
  });

  openForcedDashboardRefreshDialog$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(KanbanBoardActions.openForcedDashboardRefreshDialog),
        tap((action) => {
          this.dialog.open(RefreshBoardModalComponent, {
            maxWidth: '500px',
            disableClose: true,
            data: action.modalData,
          });
        })
      );
    },
    { dispatch: false }
  );

  recreateSnapshot$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        KanbanBoardActions.recreateSnapshot.attempt,
        BookingActions.subscribePackageSuccess,
        GatewayCommissioningActions.registerGateway.success,
        BookingActions.createTrialSubscriptionSuccess
      ),
      switchMap(() =>
        this.dashboardApiPicker
          .instance()
          .recreateSnapshot()
          .pipe(
            map(() => KanbanBoardActions.recreateSnapshot.success()),
            catchError((error) => of(KanbanBoardActions.recreateSnapshot.error({ error })))
          )
      )
    );
  });

  startFlowAfterSnapshotRecreation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.recreateSnapshot.success),
      switchMap(() => {
        const url = this.router.url;

        if (url.startsWith('/dashboard')) {
          return of(DashboardActions.getDashboard.attempt());
        }

        // Multiple return forced by following of component's logic
        if (url.includes(`/${DashboardInstallationUrls.Board}`)) {
          return [
            KanbanBoardActions.clearSelectedFilters(),
            KanbanBoardActions.getDashboard.attempt(),
          ];
        }

        if (url.includes(`/${DashboardInstallationUrls.List}`)) {
          return [
            KanbanBoardActions.clearSelectedFilters(),
            KanbanBoardActions.initialFetchInstallationList.attempt(),
          ];
        } else {
          // Commissioning, booking scenarios
          return EMPTY;
        }
      })
    );
  });

  navigateToBooking$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(KanbanBoardActions.navigateToBooking),
        concatLatestFrom(() => this.remoteConfigStore.typeOfBookingPackage$),
        tap(([{ installation, platform }, bookingType]) => {
          // Flat rate
          if (bookingType === BookingSubscriptionPackageOptions.FlatRate) {
            this.router.navigate(['/booking']);
          } else if (installation.isMultiGateway) {
            // Multi gateway
            const queryParams: BookingQueryParams = {
              installationId: installation.id,
            };

            this.router.navigate(['/booking'], {
              queryParams,
            });
          } else {
            // Regular installation
            const queryParams: BookingQueryParams = {
              boilerSerial: installation.device.vin,
              platform,
            };

            const bookingPlan = HighestSubscriptionPlanPipe.getHighestDevicePlan(
              installation.device
            );
            const isUpdatingFromPlusToPro = bookingPlan === SubscriptionPlan.Plus;

            if (isUpdatingFromPlusToPro) {
              queryParams.update = true;
              queryParams.selectedStep = BOOKING_TABS.CHECKOUT;

              this.bookingStore.getSubscriptionsDetails(
                installation.device.vin,
                isUpdatingFromPlusToPro
              );
            }

            this.router.navigate(['/booking'], {
              queryParams,
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  dispatchGetInstallationSmartNotes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KanbanBoardActions.openQuickViewPanel),
      map((action) =>
        KanbanBoardActions.getInstallationSmartNotes.attempt({
          installationId: action.installation.id,
        })
      )
    );
  });

  clearSelectedFilters$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(KanbanBoardActions.clearSelectedFilters),
        tap(() => this.kanbanBoardHelperService.filterInputSearchValue$.next(''))
      );
    },
    { dispatch: false }
  );

  private getColumnsWithVersionValueChanged(
    storeColumns: DashboardInstallationColumn[],
    fetchedColumns: DashboardColumn[]
  ): { id: number; numberOfInstallations: number; version: string }[] {
    const columnsToUpdate: { id: number; numberOfInstallations: number; version: string }[] = [];

    storeColumns.forEach((storeColumn) => {
      const matchingFetchedColumn = fetchedColumns.find(
        (fetchedColumn) => fetchedColumn.id === storeColumn.id
      );
      if (matchingFetchedColumn && storeColumn.version !== matchingFetchedColumn.version) {
        columnsToUpdate.push({
          id: storeColumn.id,
          numberOfInstallations: storeColumn.installations.length,
          version: matchingFetchedColumn.version,
        });
      }
    });

    return columnsToUpdate;
  }

  private isInBoardView(): boolean {
    const url = this.router.url;

    return url.includes(`/${DashboardInstallationUrls.Board}`);
  }

  private extendButtonFilterData(input: ButtonFilterData[]): DashboardListFilterButtonData[] {
    return input.map((buttonData) => {
      const output = {
        customName: buttonData.name,
        quantity: buttonData.value,
      };

      // Unify object data so it's clear which filter group contains a specific filter
      // because backend returns us only customName and its quantity

      switch (buttonData.name) {
        case ButtonFilterDataName.All:
          return {
            ...output,
            filterName: null,
            filterValue: null,
          };

        case ButtonFilterDataName.Error:
          return {
            ...output,
            ...KanbanBoardMappers.getSumsPayloadDataArray(errorButtonFilterDataPayload),
          };

        case ButtonFilterDataName.Offline:
          return {
            ...output,
            ...KanbanBoardMappers.getSumsPayloadDataArray(offlineButtonFilterDataPayload),
          };

        case ButtonFilterDataName.Maintenance:
          return {
            ...output,
            ...KanbanBoardMappers.getSumsPayloadDataArray(maintenanceButtonFilterDataPayload),
          };

        case ButtonFilterDataName.Anomalies:
          return {
            ...output,
            // Display 1 filter but handle as 3 - compare with variable anomaliesButtonFilterDataPayload
            ...KanbanBoardMappers.getSumsPayloadDataArray({
              [DashboardFilterName.ActiveAnomalies]: ['warning'],
            }),
          };

        case ButtonFilterDataName.Working:
          return {
            ...output,
            ...KanbanBoardMappers.getSumsPayloadDataArray(worksProperlyButtonFilterDataPayload),
          };
      }
    });
  }

  private getInitialFetchInstallationListLimit(): number {
    const innerHeight = window.innerHeight;
    const scrollbarHeight = 1550;
    if (innerHeight <= scrollbarHeight) {
      return initialNumberOfInstallationsInListView;
    } else {
      const tenInstallationsHeight = 1100;
      const additionalScrollHeight = innerHeight - scrollbarHeight;
      const multiply = Math.trunc(additionalScrollHeight / tenInstallationsHeight) + 1;

      return initialNumberOfInstallationsInListView + 10 * multiply;
    }
  }

  private getInstallationListAndItsLimit(
    filters: DashboardFilterPayload
  ): Observable<{
    installations: DashboardInstallationData[];
    cursor: string;
  }> {
    return this.dashboardApiPicker
      .instance()
      .getFilteredInstallationsV2(this.getInitialFetchInstallationListLimit(), filters)
      .pipe(
        map((response) => ({
          installations: response.data,
          cursor: response.cursor.next,
        }))
      );
  }
}
